import { useEffect, useState } from 'react';
import {
  Button,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import axios from 'axios';

export default function SubscriptionPlansPage() {
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, expired: 0 });
  const [subscribers, setSubscribers] = useState([]);
  const [planFilter, setPlanFilter] = useState('All');
  const headers = {
    headers: { Authorization: `Bearer ${localStorage.getItem('USER_TOKEN')}` },
  };

  const load = () => Promise.all([
    axios.get('/api/v1/subscriptions/admin/plans', headers),
    axios.get('/api/v1/subscriptions/admin/stats', headers),
    axios.get('/api/v1/subscriptions/admin/subscribers', headers),
  ]).then(([plansRes, statsRes, subscribersRes]) => {
    setPlans(plansRes.data.data || []);
    setStats(statsRes.data.data || {});
    setSubscribers(subscribersRes.data.data || []);
  }).catch(() => toast.error('Failed to load subscription data'));

  useEffect(() => { load(); }, []);

  const updatePlan = (id, field, value) => {
    setPlans((current) => current.map((plan) => (
      plan._id === id ? { ...plan, [field]: value } : plan
    )));
  };

  const save = async (plan) => {
    try {
      await axios.put(`/api/v1/subscriptions/admin/plans/${plan._id}`, plan, headers);
      toast.success('Plan updated');
    } catch (_) {
      toast.error('Update failed');
    }
  };

  const filteredSubscribers = planFilter === 'All'
    ? subscribers
    : subscribers.filter((subscriber) => subscriber.plan?.name === planFilter);

  const planCounts = ['Daily', 'Weekly', 'Monthly', 'Yearly'].map((name) => ({
    name,
    count: subscribers.filter((subscriber) => subscriber.plan?.name === name).length,
  }));

  return (
    <Container>
      <Typography variant="h4" mb={3}>Subscription Management</Typography>
      <Typography mb={3}>
        Total: {stats.total || 0} &nbsp; Active: {stats.active || 0} &nbsp; Expired: {stats.expired || 0}
      </Typography>
      <Stack direction="row" spacing={2} flexWrap="wrap" mb={3}>
        {planCounts.map((item) => (
          <Typography key={item.name} variant="body1">
            {item.name}: <strong>{item.count}</strong>
          </Typography>
        ))}
      </Stack>
      <Typography variant="h5" mb={2}>Subscription Pricing</Typography>
      <Stack spacing={2} maxWidth={980}>
        {plans.map((plan) => (
          <Stack key={plan._id} direction="row" spacing={1} alignItems="center">
            <TextField label="Plan" value={plan.name} disabled sx={{ flex: 1 }} />
            <TextField label="New Coins" type="number" value={plan.price}
              onChange={(event) => updatePlan(plan._id, 'price', Number(event.target.value))} sx={{ width: 130 }} />
            <TextField label="Renewal Coins" type="number" value={plan.renewalPrice ?? 0}
              onChange={(event) => updatePlan(plan._id, 'renewalPrice', Number(event.target.value))} sx={{ width: 150 }} />
            <TextField label="Days" type="number" value={plan.durationDays}
              onChange={(event) => updatePlan(plan._id, 'durationDays', Number(event.target.value))} sx={{ width: 110 }} />
            <Switch checked={plan.active} onChange={(event) => updatePlan(plan._id, 'active', event.target.checked)} />
            <Button onClick={() => save(plan)}>Save</Button>
          </Stack>
        ))}
      </Stack>
      <Typography variant="h5" mt={5} mb={2}>Subscribers</Typography>
      <FormControl size="small" sx={{ minWidth: 190, mb: 2 }}>
        <InputLabel id="subscription-plan-filter">Filter plan</InputLabel>
        <Select labelId="subscription-plan-filter" label="Filter plan" value={planFilter}
          onChange={(event) => setPlanFilter(event.target.value)}>
          <MenuItem value="All">All plans</MenuItem>
          {['Daily', 'Weekly', 'Monthly', 'Yearly'].map((name) => (
            <MenuItem key={name} value={name}>{name}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <Table size="small">
        <TableHead><TableRow>
          <TableCell>Name</TableCell><TableCell>Mobile</TableCell><TableCell>Email</TableCell>
          <TableCell>Plan</TableCell><TableCell>Purchase</TableCell><TableCell>Start</TableCell>
          <TableCell>Expiry</TableCell><TableCell>Status</TableCell><TableCell>Coins</TableCell><TableCell>Actions</TableCell>
        </TableRow></TableHead>
        <TableBody>{filteredSubscribers.map((subscriber) => (
          <TableRow key={subscriber._id}>
            <TableCell>{subscriber.user?.name || '-'}</TableCell>
            <TableCell>{subscriber.user?.phone || '-'}</TableCell>
            <TableCell>{subscriber.user?.email || '-'}</TableCell>
            <TableCell>{subscriber.plan?.name || '-'}</TableCell>
            <TableCell>{subscriber.createdAt ? new Date(subscriber.createdAt).toLocaleDateString() : '-'}</TableCell>
            <TableCell>{subscriber.startsAt ? new Date(subscriber.startsAt).toLocaleDateString() : '-'}</TableCell>
            <TableCell>{subscriber.expiresAt ? new Date(subscriber.expiresAt).toLocaleDateString() : '-'}</TableCell>
            <TableCell>{subscriber.status}</TableCell>
            <TableCell>{subscriber.plan?.price || 0}</TableCell>
            <TableCell><Button size="small" onClick={() => axios.put(`/api/v1/subscriptions/admin/subscribers/${subscriber._id}/action`, {
              action: subscriber.status === 'active' ? 'deactivate' : 'activate', reason: 'Admin action',
            }, headers).then(load).catch(() => toast.error('Action failed'))}>
              {subscriber.status === 'active' ? 'Deactivate' : 'Activate'}
            </Button></TableCell>
          </TableRow>
        ))}</TableBody>
      </Table>
    </Container>
  );
}
